import React, { useContext, useEffect, useMemo, useState } from "react";
import MapBoxField from "../../../components/map/MapBoxField";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, VStack, Input, Select, Button, Checkbox, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, useToast, Badge } from "@chakra-ui/react";
import { Navigate, useLocation } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";

export default function AdminCustomers() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneVerified, setPhoneVerified] = useState("all");
  const [cityFilter, setCityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const createDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const [editCustomer, setEditCustomer] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", address: "", city: "", phone_number: "" });
  const toast = useToast();
  const verifyDisclosure = useDisclosure();
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");

  useEffect(() => {
    if (!isSuperUser) return;
    fetchCities();
  }, [isSuperUser]);

  useEffect(() => {
    if (!isSuperUser) return;
    const qs = new URLSearchParams(location.search || "");
    const p = parseInt(qs.get("page") || "1", 10);
    setCurrentPage(p);
    fetchCustomers(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser, location.search]);

  const fetchCities = async () => {
    try {
      const { data } = await client.get("/public/cities/");
      setCities(data.results || data);
    } catch (e) {
      // ignore
    }
  };

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (searchTerm) params.set("search", searchTerm);
      if (phoneNumber) params.set("phone_number", phoneNumber);
      if (phoneVerified !== "all") params.set("phone_verified", phoneVerified);
      if (cityFilter) params.set("city", cityFilter);
      const { data } = await client.get(`/customers/?${params.toString()}`);
      const items = data.results || data;
      setCustomers(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      toast({ title: "Müşteriler alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (c) => {
    setEditCustomer(c);
    setForm({
      first_name: c.first_name || c.user?.first_name || "",
      last_name: c.last_name || c.user?.last_name || "",
      address: c.address || "",
      city: urlToId(c.city?.url) || "",
      phone_number: c.phone_number || "",
    });
    editDisclosure.onOpen();
  };

  const handleCreate = async () => {
    try {
      const payload = {
        user: { first_name: form.first_name, last_name: form.last_name },
        address: form.address,
        city: form.city || null,
        phone_number: form.phone_number,
      };
      await client.post("/customers/", payload);
      toast({ title: "Müşteri oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setForm({ first_name: "", last_name: "", address: "", city: "", phone_number: "" });
      fetchCustomers(currentPage);
    } catch (error) {
      toast({ title: "Oluşturma başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!editCustomer) return;
    try {
      const payload = {
        user: { first_name: form.first_name, last_name: form.last_name },
        address: form.address,
        city: form.city || null,
        phone_number: form.phone_number,
      };
      await client.put(editCustomer.url, payload);
      toast({ title: "Müşteri güncellendi", status: "success" });
      editDisclosure.onClose();
      setEditCustomer(null);
      fetchCustomers(currentPage);
    } catch (error) {
      toast({ title: "Güncelleme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const handleDelete = async (c) => {
    try {
      await client.delete(c.url);
      toast({ title: "Müşteri silindi", status: "success" });
      fetchCustomers(currentPage);
    } catch (error) {
      toast({ title: "Silme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const sendVerificationSMS = async (customer) => {
    try {
      await client.post(`/customers/${urlToId(customer.url)}/send_verification_sms/`, {
        phone_number: customer.phone_number,
      });
      toast({ title: "SMS gönderildi", status: "success" });
    } catch (e) {
      toast({ title: "SMS gönderme hatası", status: "error" });
    }
  };

  const openVerifyModal = (customer) => {
    setVerifyTarget(customer);
    setVerifyCode("");
    verifyDisclosure.onOpen();
  };

  const submitVerifyCode = async () => {
    if (!verifyTarget) return;
    try {
      await client.post(`/customers/${urlToId(verifyTarget.url)}/verify_customer_number/`, {
        security_code: verifyCode,
      });
      toast({ title: "Telefon doğrulandı", status: "success" });
      verifyDisclosure.onClose();
      fetchCustomers(currentPage);
    } catch (e) {
      toast({ title: "Doğrulama hatası", status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>Müşteri Yönetimi</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <HStack spacing={3}>
          <Input placeholder="Ara: ad, soyad, telefon" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onBlur={() => fetchCustomers(1)} />
          <Input placeholder="Telefon" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} w="160px" />
          <Select value={phoneVerified} onChange={(e) => setPhoneVerified(e.target.value)} w="180px">
            <option value="all">Tümü</option>
            <option value="true">Doğrulanmış</option>
            <option value="false">Doğrulanmamış</option>
          </Select>
          <Select placeholder="Şehir" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} w="220px">
            {cities.map((c) => <option key={c.url || c.name} value={urlToId(c.url)}>{c.name}</option>)}
          </Select>
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchCustomers(1); }}>Filtrele</Button>
          <Button colorScheme="green" onClick={createDisclosure.onOpen}>Yeni Müşteri</Button>
          <ExportButton
            data={(customers || []).map(c => ({
              id: urlToId(c.url),
              ad_soyad: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
              telefon: c.phone_number || '-',
              dogrulama: c.phone_verified ? 'Doğrulanmış' : 'Doğrulanmamış',
              sehir: c.city_info?.name || '-',
              adres: c.address || '-',
              son_bayi: c.last_reseller?.name || '-',
            }))}
            filename="musteriler_admin"
            columns={[
              { key: "id", label: "ID" },
              { key: "ad_soyad", label: "Ad Soyad" },
              { key: "telefon", label: "Telefon" },
              { key: "dogrulama", label: "Doğrulama" },
              { key: "sehir", label: "Şehir" },
              { key: "adres", label: "Adres" },
              { key: "son_bayi", label: "Son Bayi" },
            ]}
            sheetName="Müşteriler"
            excelOptions={{ title: "Müşteriler" }}
          />
        </HStack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Ad Soyad</Th>
              <Th>Telefon</Th>
              <Th>Doğrulama</Th>
              <Th>Şehir</Th>
              <Th>Adres</Th>
              <Th>Son Bayi</Th>
              <Th>Aksiyon</Th>
              <Th>Doğrulama Aksiyonları</Th>
            </Tr>
          </Thead>
          <Tbody>
            {customers.map((c) => {
              const isSelected = selected.has(c.url);
              return (
                <Tr key={c.url}>
                  <Td>
                    <Checkbox isChecked={isSelected} onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(c.url); else next.delete(c.url);
                      setSelected(next);
                    }} />
                  </Td>
                  <Td>{c.first_name || c.user?.first_name} {c.last_name || c.user?.last_name}</Td>
                  <Td>{c.phone_number}</Td>
                  <Td>
                    <Badge colorScheme={c.phone_verified ? "green" : "gray"}>{c.phone_verified ? "Doğrulandı" : "Bekliyor"}</Badge>
                  </Td>
                  <Td>{c.city_name || '-'}</Td>
                  <Td>{c.address || '-'}</Td>
                  <Td>{c.last_reseller?.name || '-'}</Td>
                  <Td>
                    <Button size="xs" variant="outline" onClick={() => openEdit(c)}>Düzenle</Button>
                    <Button size="xs" colorScheme="red" variant="outline" ml={2} onClick={() => handleDelete(c)}>Sil</Button>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button size="xs" onClick={() => sendVerificationSMS(c)}>SMS Gönder</Button>
                      <Button size="xs" variant="outline" onClick={() => openVerifyModal(c)}>Kod Doğrula</Button>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <Box mt={4}>
        <Pagination path={"admin/musteriler"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Müşteri Oluştur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şehir</FormLabel>
                <Select placeholder="Şehir" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}>
                  {cities.map((c) => <option key={c.url || c.name} value={urlToId(c.url)}>{c.name}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Adres</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <MapBoxField value={form.address} onChangeAddress={(addr) => setForm({ ...form, address: addr })} />
              </FormControl>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleCreate}>Oluştur</Button>
            <Button onClick={createDisclosure.onClose}>İptal</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Verify Phone Modal */}
      <Modal isOpen={verifyDisclosure.isOpen} onClose={verifyDisclosure.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Telefon Doğrula</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel>Güvenlik Kodu</FormLabel>
                <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} placeholder="SMS ile gelen kod" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={verifyDisclosure.onClose}>İptal</Button>
            <Button colorScheme="blue" onClick={submitVerifyCode}>Doğrula</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Müşteri Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şehir</FormLabel>
                <Select placeholder="Şehir" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}>
                  {cities.map((c) => <option key={c.url || c.name} value={urlToId(c.url)}>{c.name}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Adres</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <MapBoxField value={form.address} onChangeAddress={(addr) => setForm({ ...form, address: addr })} />
              </FormControl>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleUpdate}>Güncelle</Button>
            <Button onClick={() => { editDisclosure.onClose(); setEditCustomer(null); }}>İptal</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
